<template>
  <div
    ref="referenceRef"
    class="relative"
  >
    <div ref="inputRef">
      <SfInput
        :id="config"
        v-model="inputModel"
        :aria-label="`${$t(fields[config].label)} Search`"
        :placeholder="$t(fields[config].label)"
        :data-testid="`form-input__${config}`"
        :invalid="errors.length > 0"
        :type="fields[config].type"
        :disabled="isDisabled(config)"
        :required="isRequired(config)"
        @keydown.esc.prevent="reset"
        @keydown.tab.prevent="handleTabInput"
        @keydown.arrow-down.prevent="handleArrowDown"
      />
    </div>
    <div
      v-if="isOpen"
      ref="floatingRef"
      :style="style"
      class="inset-x-0 z-10"
      data-test-id="addressAutoComplete"
    >
      <div
        v-if="isLoading"
        class="flex h-20 w-full items-center justify-center rounded-md border border-solid border-neutral-100 bg-white py-2 drop-shadow-md"
      >
        <SfLoaderCircular />
      </div>
      <ul
        v-else-if="result?.addresses.length > 0"
        ref="dropdownListRef"
        class="rounded-md border border-solid border-neutral-100 bg-white py-2 drop-shadow-md"
      >
        <li
          v-for="(suggestion, index) in sortedSuggestions"
          :key="suggestion.freetext"
          @keydown.arrow-up.prevent=""
          @keydown.arrow-down.prevent=""
        >
          <SfListItem
            tag="button"
            type="button"
            class="flex justify-start"
            @click="() => selectValue(suggestion)"
            @keydown.enter.space.prevent="selectValue(suggestion)"
            @keydown.tab="index === sortedSuggestions.length - 1 ? close() : null"
          >
            <p class="text-left">
              <span>{{ suggestion.freetext }}</span>
            </p>
          </SfListItem>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { unrefElement, watchDebounced } from '@vueuse/core';
import {
  SfInput,
  SfListItem,
  SfLoaderCircular,
  useDisclosure,
  useDropdown,
  useTrapFocus,
} from '@storefront-ui/vue';
import type { Form } from '../DynamicForm/checkout-form';

type Address = {
  streetName: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  [key: string]: string | undefined;
};

interface Props {
  fields: Form;
  config: string;
  errors: string[];
  disabled: boolean;
  address: Address;
}

type ResponseAddress = {
  freetext: string;
  address: Address;
};

const emits = defineEmits(['update:modelValue', 'update:address']);

const { fields, config, errors, disabled, address } = defineProps<Props>();

const { autocomplete, result, resetResults } = useAddress();
const { $flags } = useNuxtApp();

const doNotTrigger = ref(false);

const inputModel = defineModel({
  type: String,
  default: '',
});

const inputRef = ref();
const dropdownListRef = ref<HTMLElement>();
const isLoading = ref(false);
const { isOpen, close, open } = useDisclosure();
const { referenceRef, floatingRef, style } = useDropdown({
  isOpen,
  onClose: close,
  placement: 'bottom-start',
});
const { focusNextElement, focusPreviousElement } = useFocus();

const { focusables: focusableElements } = useTrapFocus(dropdownListRef as Ref<HTMLElement>, {
  trapTabs: false,
  arrowKeysUpDown: true,
  activeState: isOpen,
  initialFocus: false,
});

const focusInput = () => {
  const inputEl = unrefElement(inputRef)?.querySelector('input');
  inputEl?.focus();
};

const reset = () => {
  resetResults();
  focusInput();
  close();
};

const selectValue = (value: ResponseAddress) => {
  const formAddress = {
    streetName: value.address.streetName,
    streetNumber: value.address.streetNumber,
    postalCode: value.address.postalCode,
    city: value.address.city,
  };
  emits('update:address', formAddress);
  reset();
};

const handleArrowDown = () => {
  if (isOpen && focusableElements.value.length > 0) {
    focusNextElement();
  }
};

const handleTabInput = (event: KeyboardEvent) => {
  if (isOpen.value) {
    close();
  }

  emits('update:modelValue', inputModel.value);

  if (event.shiftKey) {
    focusPreviousElement();

    return;
  }

  focusNextElement();
};

watch(inputModel, () => {
  if (inputModel.value === '') {
    reset();
  }
});

watchDebounced(
  inputModel,
  async () => {
    if (doNotTrigger.value || $flags.hasnt('address.autocomplete')) {
      doNotTrigger.value = false;

      return;
    }
    if (inputModel.value) {
      open();
      isLoading.value = true;
      try {
        await autocompleteRequest();
      }
      catch {
        close();
      }
      isLoading.value = false;
    }
  },
  { debounce: 500 },
);

const autocompleteRequest = async () => {
  const requestAddress = {
    ...address,
    [config]: inputModel.value,
  };

  const requestAddressAsString = Object.values(requestAddress)
    .filter(value => !!value)
    .join(', ');

  await autocomplete(requestAddressAsString);

  emits('update:modelValue', inputModel.value);
};

const isDisabled = (config: string) => disabled || !!fields[config]?.disabled;

const isRequired = (config: string) => {
  const rules = fields?.[config]?.rules;
  if (!rules) {
    return false;
  }
  if (typeof rules === 'object') {
    return rules.required as boolean;
  }
  return rules.split('|').includes('required');
};

const isAbove = ref(false);

const sortedSuggestions = computed(() => {
  if (!result.value?.addresses) {
    return [];
  }

  if (isAbove.value) {
    return result.value.addresses.slice().reverse();
  }

  return result.value.addresses;
});

const updateSortedSuggestions = () => {
  if (!result.value?.addresses) {
    return;
  }
  if (
    (dropdownListRef.value?.getBoundingClientRect().y ?? 0)
    < (inputRef.value?.getBoundingClientRect().y ?? 0)
  ) {
    isAbove.value = true;

    return;
  }
  isAbove.value = false;
};

onMounted(() => {
  document.addEventListener('scroll', updateSortedSuggestions);
});

onBeforeUnmount(() => {
  document.removeEventListener('scroll', updateSortedSuggestions);
});

watch(
  () => address[config],
  (newVal) => {
    if (inputModel.value !== newVal) {
      inputModel.value = newVal ?? '';
      doNotTrigger.value = true;
    }
  },
  { immediate: true },
);
</script>
